export type DACNodeId = string;

export type DACNode<T> = {
  label?: string;
  value: number;
  from: DACNodeId | null;
  to: DACNodeId[];
  metaData: T;
};

export type DAC<T> = {
  [key in DACNodeId]: DACNode<T>;
};

export function findBestPath<T>(
  tree: DAC<T>,
  currentKey: DACNodeId = "start",
  currentPath: DACNodeId[] = [],
): DACNodeId[] | null {
  const currentNode = tree[currentKey];

  if (currentNode.to.length === 0) {
    return [...currentPath, currentKey];
  }

  let bestPath: DACNodeId[] | null = null;
  let highestValue: number = -1;

  for (const childKey of currentNode.to) {
    const childPath = findBestPath(tree, childKey, [
      ...currentPath,
      currentKey,
    ]);

    if (childPath) {
      const childValue = tree[childPath[childPath.length - 1]].value;
      if (childValue > highestValue) {
        bestPath = childPath;
        highestValue = childValue;
      }
    }
  }

  return bestPath;
}

export function findPathToNode<T>(
  tree: DAC<T>,
  targetId: DACNodeId,
): DACNodeId[] | null {
  const result: DACNodeId[] = [];
  function traverse(nodeId: DACNodeId): boolean {
    if (!tree[nodeId]) return false;
    result.push(nodeId);
    if (nodeId === targetId) return true;
    for (const child of tree[nodeId].to) {
      if (traverse(child)) return true;
    }
    result.pop();
    return false;
  }
  traverse("start");
  return result.length ? result : null;
}

function computeMaxDownstreamValues<T>(
  tree: DAC<T>,
): Record<DACNodeId, number> {
  const cache: Record<DACNodeId, number> = {};

  function dfs(nodeId: DACNodeId): number {
    if (cache[nodeId] !== undefined) return cache[nodeId];
    const node = tree[nodeId];
    if (!node || node.to.length === 0) {
      cache[nodeId] = node?.value ?? -Infinity;
      return cache[nodeId];
    }
    const bestChildValue = Math.max(...node.to.map(dfs));
    cache[nodeId] = Math.max(node.value, bestChildValue);
    return cache[nodeId];
  }

  Object.keys(tree).forEach(dfs);
  return cache;
}

export function isPartOfPath(
  parent: DACNodeId,
  child: DACNodeId,
  path: DACNodeId[],
): boolean {
  return path.includes(parent) && path.includes(child);
}

export function DACAsArray<T>(tree: DAC<T>): DACNodeId[][] {
  const result: DACNodeId[][] = [];
  const visited: { [key in DACNodeId]?: boolean } = {};
  const queue: [DACNodeId, number][] = [["start", 0]];
  const bestPath = findBestPath(tree);
  const maxDownstream = computeMaxDownstreamValues(tree);

  while (queue.length > 0) {
    const [currentKey, level] = queue.shift()!;
    if (!visited[currentKey]) {
      visited[currentKey] = true;
      if (!result[level]) result[level] = [];
      result[level].push(currentKey);

      for (const childKey of tree[currentKey].to) {
        queue.push([childKey, level + 1]);
      }
    }
  }

  for (let i = 0; i < result.length; i++) {
    result[i].sort((a, b) => {
      const aInBest = bestPath?.[i] === a;
      const bInBest = bestPath?.[i] === b;
      if (aInBest) return -1;
      if (bInBest) return 1;

      const aMax = maxDownstream[a] ?? -Infinity;
      const bMax = maxDownstream[b] ?? -Infinity;
      return bMax - aMax;
    });
  }

  return result;
}
